import { ChangeDetectionStrategy, Component } from '@angular/core';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { ICellRendererParams } from 'ag-grid-enterprise';

/** What the column says about one template, and which chip says it. */
type FieldMappingState =
  'not-applicable' | 'standard' | 'mapped' | 'unmapped' | 'unreadable';

/**
 * Mapping health for one ESign template row.
 *
 * Answers "which clients have fields outside the standard set?" at a glance,
 * which until now needed opening templates one at a time. The row carries
 * `mappingFingerprint` (null when the template has no mapping of its own), and
 * the grid joins in the unmapped-field report so a template with gaps can say
 * how many rather than only that it has some.
 */
@Component({
  selector: 'app-field-mapping-cell-renderer',
  templateUrl: './field-mapping-cell-renderer.component.html',
  styleUrls: ['./field-mapping-cell-renderer.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class FieldMappingCellRendererComponent implements ICellRendererAngularComp {
  public state: FieldMappingState = 'not-applicable';
  public unmappedFieldCount = 0;

  public agInit(params: ICellRendererParams): void {
    this.resolveState(params);
  }

  public refresh(params: ICellRendererParams): boolean {
    this.resolveState(params);

    return true;
  }

  private resolveState(params: ICellRendererParams): void {
    const { data } = params;

    // A DocuSign template is stamped by DocuSign, so VIVE's mapping says
    // nothing about it.
    //
    // Reads a flag the grid resolves per page, NOT `data.ESignProvider.name`:
    // the v2 grid route's `attributes` is an allow-list that pulls no
    // associations, so the row never carries the provider object and that
    // check was undefined on every row — which made this column render "—"
    // for everything, always.
    if (!data?.isViveTemplate) {
      this.state = 'not-applicable';
      this.unmappedFieldCount = 0;
      return;
    }

    // Joined in by the grid from the unmapped-field report — one call for the
    // whole page rather than one per row.
    const report = data?.unmappedFieldReport;

    if (report?.readError) {
      this.state = 'unreadable';
      this.unmappedFieldCount = 0;
      return;
    }

    if (report?.unmappedFieldNames?.length) {
      this.state = 'unmapped';
      this.unmappedFieldCount = report.unmappedFieldNames.length;
      return;
    }

    this.unmappedFieldCount = 0;
    // NULL means the template has no mapping of its own and is stamped by
    // VIVE's standard handling — which is every client today, and is not a gap.
    this.state = data?.mappingFingerprint ? 'mapped' : 'standard';
  }
}
